import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { 
  Sprout, Briefcase, Users, Heart, DollarSign, Activity, BookOpen, Palette, Sparkles, 
  Target, Plus, RefreshCw, Clock, CheckCircle, Loader2, Trash2, MessageSquare
} from 'lucide-react';
import ObjectiveEditModal from './ObjectiveEditModal';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { name: 'Life', Icon: Sprout, color: 'bg-blue-500/20', textColor: 'text-blue-300', borderColor: 'border-blue-500/30' },
  { name: 'Work', Icon: Briefcase, color: 'bg-purple-500/20', textColor: 'text-purple-300', borderColor: 'border-purple-500/30' },
  { name: 'Family', Icon: Users, color: 'bg-pink-500/20', textColor: 'text-pink-300', borderColor: 'border-pink-500/30' },
  { name: 'Relationship', Icon: Heart, color: 'bg-red-500/20', textColor: 'text-red-300', borderColor: 'border-red-500/30' },
  { name: 'Financial', Icon: DollarSign, color: 'bg-green-500/20', textColor: 'text-green-300', borderColor: 'border-green-500/30' },
  { name: 'Health', Icon: Activity, color: 'bg-orange-500/20', textColor: 'text-orange-300', borderColor: 'border-orange-500/30' },
  { name: 'Education', Icon: BookOpen, color: 'bg-indigo-500/20', textColor: 'text-indigo-300', borderColor: 'border-indigo-500/30' },
  { name: 'Personal', Icon: Palette, color: 'bg-yellow-500/20', textColor: 'text-yellow-300', borderColor: 'border-yellow-500/30' },
  { name: 'Spiritual', Icon: Sparkles, color: 'bg-cyan-500/20', textColor: 'text-cyan-300', borderColor: 'border-cyan-500/30' },
  { name: 'Other', Icon: Sparkles, color: 'bg-gray-500/20', textColor: 'text-gray-300', borderColor: 'border-gray-500/30' },
];

function LifeObjectives({ objectives, onUpdate, onDelete, selectedObjective, onRecategorize, clientId, onAdd }) {
  const { user } = useAuth();
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('Life');
  const [editTimeline, setEditTimeline] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObjective, setNewObjective] = useState('');
  const [newCategory, setNewCategory] = useState('Life');
  const [newTimeline, setNewTimeline] = useState('');
  const [modalObjective, setModalObjective] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Group objectives by category
  const objectivesByCategory = useMemo(() => {
    const grouped = {};
    CATEGORIES.forEach(cat => {
      grouped[cat.name] = [];
    });
    
    objectives.forEach(obj => {
      const category = obj.category || 'Life';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(obj);
    });
    
    // Return only categories that have objectives
    return Object.entries(grouped)
      .filter(([_, objs]) => objs.length > 0)
      .sort(([a], [b]) => {
        // Sort by category order in CATEGORIES array
        const aIndex = CATEGORIES.findIndex(c => c.name === a);
        const bIndex = CATEGORIES.findIndex(c => c.name === b);
        return aIndex - bIndex;
      });
  }, [objectives]);

  const getCategoryInfo = (categoryName) => {
    return CATEGORIES.find(c => c.name === categoryName) || CATEGORIES[CATEGORIES.length - 1];
  };

  const handleEdit = (objective) => {
    setModalObjective(objective);
    setIsModalOpen(true);
  };

  const handleModalSave = (updatedObjective) => {
    onUpdate(updatedObjective.id, updatedObjective.objective, updatedObjective.status, updatedObjective.category, updatedObjective.timeline);
    // Update the objective in the local list if needed
    setIsModalOpen(false);
    setModalObjective(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalObjective(null);
  };

  const handleSave = (id, status, category, timeline) => {
    onUpdate(id, editText, status, category, timeline);
    setEditingId(null);
    setEditText('');
    setEditCategory('Life');
    setEditTimeline('');
  };

  const handleAddObjective = async () => {
    if (!newObjective.trim()) {
      toast('Please enter an objective');
      return;
    }
    
    if (onAdd) {
      await onAdd({
        objective: newObjective.trim(),
        category: newCategory,
        timeline: newTimeline.trim() || null
      });
      setNewObjective('');
      setNewCategory('Life');
      setNewTimeline('');
      setShowAddForm(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditText('');
    setEditCategory('Life');
    setEditTimeline('');
  };

  const handleStatusToggle = (objective) => {
    const newStatus = objective.status === 'active' ? 'completed' : 'active';
    onUpdate(objective.id, objective.objective, newStatus, objective.category || 'Life', objective.timeline || null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Detail view when a specific objective is selected
  if (selectedObjective) {
    const categoryInfo = getCategoryInfo(selectedObjective.category || 'Life');
    
    return (
      <>
        <ObjectiveEditModal
          objective={modalObjective}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          userRole={user?.role}
        />
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Target className="w-6 h-6" />
            <span>Objective Details</span>
          </h3>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Created {formatDate(selectedObjective.created_at)}</p>
        </div>

        {editingId === selectedObjective.id ? (
          <div>
            <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Edit objective:</label>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] mb-4 resize-none text-base"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              rows="4"
            />
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Category:</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.name} value={cat.name} className="bg-gray-800">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Timeline:</label>
                <input
                  type="text"
                  value={editTimeline}
                  onChange={(e) => setEditTimeline(e.target.value)}
                  placeholder="e.g., 3 months, by end of year"
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleSave(selectedObjective.id, selectedObjective.status, editCategory, editTimeline)}
                className="bg-green-600 hover:bg-green-700 px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={{ color: 'var(--text-primary)' }}
              >
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="hover:bg-gray-700 px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-black/20 p-5 rounded-xl mb-6">
              <div className="flex items-center gap-2 mb-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${categoryInfo.color} ${categoryInfo.textColor} ${categoryInfo.borderColor} border`}>
                  {React.createElement(categoryInfo.Icon, { className: "w-3.5 h-3.5" })}
                  {categoryInfo.name}
                </span>
              </div>
              <div className="flex items-start gap-3 mb-4">
                <input
                  type="checkbox"
                  checked={selectedObjective.status === 'completed'}
                  onChange={() => handleStatusToggle(selectedObjective)}
                  className="mt-1 h-5 w-5 text-[var(--accent)] rounded focus:ring-[var(--accent)] cursor-pointer"
                />
                <p className={`flex-1 text-lg ${selectedObjective.status === 'completed' ? 'line-through' : ''}`} style={{ color: selectedObjective.status === 'completed' ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                  {selectedObjective.objective}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  selectedObjective.status === 'completed'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-[var(--accent)]/20 text-[var(--accent)] border border-[var(--accent)]/30'
                }`}>
                  {selectedObjective.status === 'completed' ? (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Completed</span>
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Active</span>
                    </>
                  )}
                </div>
                {selectedObjective.timeline && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{selectedObjective.timeline}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Comments Display */}
            {selectedObjective.comments && selectedObjective.comments.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                  <MessageSquare className="w-4 h-4" />
                  Comments & Progress
                </h4>
                <div className="space-y-2">
                  {selectedObjective.comments.map((comment) => (
                    <div key={comment.id} className="bg-black/20 p-3 rounded-lg">
                      <p className="text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{comment.comment}</p>
                      <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                        <Clock className="w-3 h-3" />
                        {formatDate(comment.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => handleEdit(selectedObjective)}
                className="bg-[var(--accent)]/20 hover:bg-[var(--accent)]/30 text-[var(--accent)] px-5 py-2.5 rounded-xl text-sm font-medium transition-all border border-[var(--accent)]/30"
              >
                Edit Objective
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Delete this objective?')) {
                    onDelete(selectedObjective.id);
                  }
                }}
                className="bg-red-500/20 hover:bg-red-500/30 text-red-300 px-5 py-2.5 rounded-xl text-sm font-medium transition-all border border-red-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  // List view showing all objectives grouped by category
  return (
    <>
      <ObjectiveEditModal
        objective={modalObjective}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        userRole={user?.role}
      />
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Target className="w-6 h-6" />
            <span>Life Board</span>
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            {objectives.filter(obj => obj.status === 'active').length} active, {objectives.filter(obj => obj.status === 'completed').length} completed
          </p>
        </div>
        <div className="flex gap-2">
          {onAdd && (
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-[var(--accent)]/20 hover:bg-[var(--accent)]/30 text-[var(--accent)] px-4 py-2 rounded-lg text-sm transition-all border border-[var(--accent)]/30 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create New</span>
            </button>
          )}
          {onRecategorize && objectives.some(obj => !obj.category || obj.category === 'Life') && (
            <button
              onClick={onRecategorize}
              className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 px-4 py-2 rounded-lg text-sm transition-all border border-blue-600/30"
              title="Automatically recategorize objectives currently set to 'Life'"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Recategorize</span>
            </button>
          )}
        </div>
      </div>

      {/* Add Objective Form */}
      {showAddForm && onAdd && (
        <div className="mb-6 bg-white/5 rounded-xl border border-white/10 p-4">
          <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>Add New Objective</h4>
          <div className="space-y-3">
            <textarea
              value={newObjective}
              onChange={(e) => setNewObjective(e.target.value)}
              placeholder="Enter the objective..."
              className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none text-sm"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              rows="2"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.name} value={cat.name} className="bg-gray-800">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>Timeline (optional)</label>
                <input
                  type="text"
                  value={newTimeline}
                  onChange={(e) => setNewTimeline(e.target.value)}
                  placeholder="e.g., 3 months, by end of year"
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddObjective}
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ color: 'var(--text-primary)' }}
              >
                Add Objective
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewObjective('');
                  setNewCategory('Life');
                  setNewTimeline('');
                }}
                className="hover:bg-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {objectives.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-tertiary)' }} />
          <p className="text-base mb-2" style={{ color: 'var(--text-tertiary)' }}>No objectives yet</p>
          {onAdd && (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Click "Add Objective" to create your first objective</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {objectivesByCategory.map(([categoryName, categoryObjectives]) => {
            const categoryInfo = getCategoryInfo(categoryName);
            const activeCount = categoryObjectives.filter(obj => obj.status === 'active').length;
            const completedCount = categoryObjectives.filter(obj => obj.status === 'completed').length;
            
            return (
              <div
                key={categoryName}
                className={`bg-white/5 backdrop-blur-xl rounded-2xl border ${categoryInfo.borderColor} p-5 hover:bg-white/10 transition-all`}
              >
                {/* Category Header */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    {React.createElement(categoryInfo.Icon, { className: `w-5 h-5 ${categoryInfo.textColor}` })}
                    <h4 className={`text-lg font-semibold ${categoryInfo.textColor}`}>
                      {categoryName}
                    </h4>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {activeCount + completedCount}
                  </span>
                </div>
                
                {/* Objectives List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {categoryObjectives.map((objective) => (
                    <div
                      key={objective.id}
                      className={`bg-black/20 rounded-lg p-3 transition-all ${
                        objective.status === 'completed' ? 'opacity-50' : ''
                      }`}
                    >
                      {editingId === objective.id ? (
                        <div>
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] mb-2 resize-none text-sm"
                            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                            rows="2"
                          />
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                              >
                                {CATEGORIES.map(cat => (
                                  <option key={cat.name} value={cat.name} className="bg-gray-800">
                                    {cat.icon} {cat.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <input
                                type="text"
                                value={editTimeline}
                                onChange={(e) => setEditTimeline(e.target.value)}
                                placeholder="Timeline (optional)"
                                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSave(objective.id, objective.status, editCategory, editTimeline)}
                              className="bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="hover:bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer hover:bg-black/30 transition-all rounded-lg p-2 -m-2"
                          onClick={() => handleEdit(objective)}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={objective.status === 'completed'}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusToggle(objective);
                              }}
                              className="mt-0.5 h-4 w-4 text-[var(--accent)] rounded focus:ring-[var(--accent)] cursor-pointer flex-shrink-0"
                            />
                            <div className="flex-1">
                              <p className={`text-sm ${objective.status === 'completed' ? 'line-through' : ''}`} style={{ color: objective.status === 'completed' ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                                {objective.objective}
                              </p>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                {objective.created_at && (
                                  <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                                    <span>Created {new Date(objective.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                  </p>
                                )}
                                {objective.timeline && (
                                  <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                                    <Clock className="w-3 h-3" />
                                    <span>{objective.timeline}</span>
                                  </p>
                                )}
                                {objective.comments && objective.comments.length > 0 && (
                                  <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                                    <MessageSquare className="w-3 h-3" />
                                    <span>{objective.comments.length} comment{objective.comments.length !== 1 ? 's' : ''}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-6 mt-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleEdit(objective)}
                              className="text-[var(--accent)] hover:text-[var(--accent-hover)] text-xs transition-colors"
                            >
                              Edit
                            </button>
                            {onDelete && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Delete this objective?')) {
                                    onDelete(objective.id);
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 text-xs transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Category Footer Stats */}
                {activeCount > 0 || completedCount > 0 ? (
                  <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    <span>{activeCount} active</span>
                    {completedCount > 0 && <span>{completedCount} completed</span>}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}

export default LifeObjectives;
